<script setup lang="ts">
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import dayjs from 'dayjs';
import LZString from 'lz-string';

import type { TranslationForm, TranslationFormItem } from '@/types/translation';

const formData = reactive<TranslationForm>({
	targets: [
		{
			arrive: '',
			depart: '',
			location: '',
			experiences: {
				tradition: false,
				food: false,
				nature: false,
				anime: false,
				workshop: false,
			},
			coordinates: null,
			countryCode: '',
		},
	],
});

const router = useRouter();
const { initGeocoder, fetchLocation } = useGoogleMaps();
const notificationStore = useNotificationStore();

const isGeocoderInitialized = ref(false);
const geocoderRetryCount = ref(0);
const isSubmit = ref(false);
const isGeocoding = ref(false);

const isLoading = computed(() => false);
const isAtDestinationLimit = computed(() => formData.targets?.length >= VISITING_JAPAN_DESTINATION_LIMIT);

const rules = {
	targets: {
		$each: helpers.forEach({
			arrive: {
				required: helpers.withMessage('Please enter your travel dates.', (value: string) => {
					if (!isSubmit.value) {
						return true;
					}
					return !!value;
				}),
			},
			depart: {
				required: helpers.withMessage('Please enter your travel dates.', (value: string) => {
					if (!isSubmit.value) {
						return true;
					}
					return !!value;
				}),
				isAfterStartDate: helpers.withMessage(
					'Departure date must be on or after the arrival date.',
					(value: string, model: any) => {
						if (!isSubmit.value || !value) {
							return true;
						}
						return dayjs(value).isSameOrAfter(model.arrive);
					},
				),
			},
			location: {
				required: helpers.withMessage('Please enter your destination.', (value: string) => {
					if (!isSubmit.value) {
						return true;
					}
					return !!value;
				}),
			},
		}),
	},
};

const v$ = useVuelidate(rules, formData);

function handleAddDestination() {
	v$.value.$reset();
	isSubmit.value = false;

	const newDestination = {
		arrive: '',
		depart: '',
		location: '',
		experiences: {
			tradition: false,
			food: false,
			nature: false,
			anime: false,
			workshop: false,
		},
		coordinates: null,
		countryCode: '',
	};
	const clonedFormData: TranslationFormItem[] = deepClone(formData.targets);
	clonedFormData?.push(newDestination);
	formData.targets = clonedFormData;
}

function handleDestinationChange(newData: TranslationFormItem, changeIndex: number) {
	if (!formData.targets?.length) {
		return;
	}

	formData.targets = formData.targets.map((destinationItem, index) => {
		if (index === changeIndex) {
			return {
				...destinationItem,
				...newData,
			};
		}
		return destinationItem;
	});
}

function handleDestinationRemove(removeIndex: number) {
	v$.value.$reset();
	isSubmit.value = false;

	if (!formData.targets?.length) {
		return;
	}
	const clonedFormData: TranslationFormItem[] = deepClone(formData.targets);
	formData.targets = clonedFormData.filter((_codeItem, index) => {
		return index !== removeIndex;
	});
}

function transformTags(exp: TranslationFormItem['experiences']) {
	const experienceIds = {
		tradition: DISCOVERY_TAGS.CULTURE,
		food: DISCOVERY_TAGS.FOOD,
		nature: DISCOVERY_TAGS.NATURE,
		anime: DISCOVERY_TAGS.ANIME,
		workshop: DISCOVERY_TAGS.WORKSHOP,
	};
	return Object.entries(exp)
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		.filter(([_key, value]) => value)
		.map(([key]) => experienceIds[key as keyof typeof experienceIds])
		.join(',');
}

async function initSearch() {
	isGeocoding.value = true;
	const criteriaData = [];

	for (const [index, destination] of formData.targets.entries()) {
		try {
			if (!isGeocoderInitialized.value) {
				throw new Error('Geocoder not initialized');
			}

			const data = {
				index,
				arrive: destination.arrive,
				depart: destination.depart,
				location: destination.location,
				tags: transformTags(destination.experiences),
				coordinates: `${destination.coordinates?.lat},${destination.coordinates?.lng}`,
				countryCode: 'jp', // Allow only japan
			};

			if (destination.location && !destination.coordinates) {
				// eslint-disable-next-line no-await-in-loop
				const { coordinates } = await fetchLocation(destination.location);
				data.coordinates = coordinates?.toUrlValue() || '';
			}
			criteriaData.push(data);
		} catch (error) {
			const data = {
				index,
				arrive: destination.arrive,
				depart: destination.depart,
				location: '', // Remove location parameter as requested
				tags: transformTags(destination.experiences),
				coordinates: '',
				countryCode: 'jp', // Allow only japan
			};
			criteriaData.push(data);
		}
	}

	const compressedCriteria = LZString.compressToEncodedURIComponent(JSON.stringify(criteriaData));

	router.push({
		name: ROUTE.VISIT_JAPAN_SEARCH_RESULTS.NAME,
		query: {
			[DISCOVERY_JAPAN_QUERY_NAME.SEARCH]: compressedCriteria,
		},
	});
	isGeocoding.value = false;
}

function handleSubmit() {
	isSubmit.value = true;
	v$.value.$touch();
	if (v$.value.$error) {
		return;
	}
	initSearch();
	isSubmit.value = false;
}

async function initializeGeocoderWithRetry() {
	try {
		if (window.google?.maps) {
			await initGeocoder();
			isGeocoderInitialized.value = true;
		} else if (geocoderRetryCount.value < 3) {
			geocoderRetryCount.value++;
			setTimeout(initializeGeocoderWithRetry, 1000);
		} else {
			notificationStore.showErrorNotification(COMMON_ERROR_MESSAGE.REFRESH_PAGE);
		}
	} catch (error) {
		if (geocoderRetryCount.value < 3) {
			geocoderRetryCount.value++;
			setTimeout(initializeGeocoderWithRetry, 1000);
		} else {
			notificationStore.showErrorNotification(COMMON_ERROR_MESSAGE.REFRESH_PAGE);
		}
	}
}

onMounted(() => initializeGeocoderWithRetry());
</script>

<template>
  <div class="event-form-page">
    <div class="title-container">
      <h2 class="title">
        Create your Japan experience
      </h2>
      <p class="subtitle">
        Provide a few basic details, and Peatix will find local events for you during your visit to Japan!
      </p>
    </div>

    <form class="form-container">
      <div
        v-if="formData.targets?.length"
        class="destinations-list"
      >
        <JapanDiscoveryInputItem
          v-for="(destination, index) in formData.targets"
          :key="index"
          :destination-index="index"
          :data="destination"
          :disabled="isLoading"
          :errors="v$.targets.$each.$response.$errors"
          @on-change="
            (emittedDestination: TranslationFormItem) =>
              handleDestinationChange(emittedDestination, index)
          "
          @on-remove="handleDestinationRemove(index)"
          @on-press-enter="handleSubmit"
        />
      </div>
      <BaseButton
        v-if="!isAtDestinationLimit"
        class="add-btn"
        variant="link"
        color="primary"
        size="sm"
        @click="handleAddDestination"
      >
        Add another destination
      </BaseButton>
    </form>

    <div class="button-container">
      <BaseButton
        class="submit-btn"
        :loading="isGeocoding"
        :disabled="isGeocoding"
        @click="handleSubmit"
      >
        <template v-if="isGeocoding">
          <BaseProgressCircular
            :size="24"
            color="#444CE7"
          />
        </template>
        <template v-else>
          Find local events
        </template>
      </BaseButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.event-form-page {
  display: flex;
  flex-direction: column;
  gap: spacings-get(8);
  padding: spacings-get(10) 0 0;
  max-width: rem(368);
  @include fonts-get(regular, text-sm);

  @include media-query-max(mobile) {
    padding-top: 0;
  }

  .title-container {
    display: flex;
    flex-direction: column;
    gap: spacings-get(2);

    .title {
      @include fonts-get(semibold, display-sm);
      color: colors-get(gray, 900);
    }

    .subtitle {
      @include fonts-get(medium, text-sm);
      color: colors-get(gray, 600);
    }
  }

  .form-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    gap: spacings-get(4);
    color: colors-get(gray, 700);

		.destinations-list {
			width: 100%;
		}
  }

	.button-container {
		position: sticky;
		bottom: 0;
		display: flex;
		justify-content: center;

		.submit-btn {
			width: 100%;
		}
	}

  .add-btn {
    padding: 0;
    @include fonts-get(regular, text-sm);
  }
}
</style>
